import {
  ArrowRight,
  ClipboardList,
  Clock,
  Search,
  Sparkles,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { Link } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Wordmark } from '@/components/wordmark';

/**
 * Landingspagina voor niet-ingelogde bezoekers (design-skill: ui_kits/webapp/
 * Landing.jsx). Geen verzonnen quotes, cijfers of logo's; functies die er nog
 * niet zijn krijgen "Binnenkort". Gradients alleen op marketingvlakken.
 */
export function LandingPage() {
  return (
    <div className="min-h-dvh overflow-x-hidden">
      <div className="relative isolate">
        <Blobs />
        <header className="mx-auto flex h-16 max-w-7xl items-center gap-2 px-4 md:px-8">
          <Wordmark />
          <span className="flex-1" />
          <Button asChild variant="ghost" className="hidden sm:inline-flex">
            <a href="#hoe-het-werkt">Hoe het werkt</a>
          </Button>
          <Button asChild variant="ghost">
            <Link to="/inloggen">Inloggen</Link>
          </Button>
          <Button asChild className="hidden sm:inline-flex">
            <Link to="/registreren">Account aanmaken</Link>
          </Button>
        </header>

        <section className="mx-auto grid max-w-7xl gap-10 px-4 pt-10 pb-14 md:px-8 lg:grid-cols-[1fr_1.1fr] lg:items-center lg:gap-16 lg:pt-18 lg:pb-24">
          <div>
            <Badge variant="outline">Voor volleybaltrainers</Badge>
            <h1 className="mt-5 text-display font-bold lg:text-display-lg">
              Je training staat klaar voor je de{' '}
              <span className="bg-linear-to-r from-primary via-[oklch(0.645_0.246_16.439)] to-[oklch(0.769_0.188_70.08)] bg-clip-text text-transparent">
                zaal in loopt.
              </span>
            </h1>
            <p className="mt-5 mb-7 max-w-[36ch] text-lg text-pretty text-muted-foreground">
              Vind oefeningen, deel ze binnen je club en houd je eigen bibliotheek bij. Op je
              telefoon in de sporthal én achter je laptop thuis.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button asChild size="lg">
                <Link to="/registreren">
                  Account aanmaken
                  <ArrowRight aria-hidden="true" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/inloggen">Inloggen</Link>
              </Button>
            </div>
          </div>
          <Preview />
        </section>
      </div>

      <section className="mx-auto grid max-w-7xl gap-4 px-4 py-14 md:grid-cols-3 md:px-8 lg:py-22">
        <Benefit
          icon={Search}
          title="Vind snel een oefening"
          text="Zoek op categorie, niveau, aantal spelers en duur. In je eigen bibliotheek, die van je club en de openbare."
        />
        <Benefit
          icon={ClipboardList}
          title="Bouw je training in minuten"
          text="Zet oefeningen op volgorde, pas de duur aan en zie direct of je binnen de tijd blijft."
          soon
        />
        <Benefit
          icon={Sparkles}
          title="Van video naar oefening"
          text="Upload een filmpje van je eigen training. Je krijgt een uitgeschreven concept dat jij controleert en aanvult."
          soon
        />
      </section>

      <div className="bg-[radial-gradient(50%_70%_at_15%_50%,color-mix(in_oklab,var(--primary)_10%,transparent),transparent_70%)]">
        <section
          id="hoe-het-werkt"
          className="mx-auto grid max-w-7xl scroll-mt-4 gap-8 px-4 py-14 md:px-8 lg:py-22"
        >
          <h2 className="max-w-[16ch] text-h1 font-bold lg:text-h1-lg">Zo werkt het</h2>
          <ol className="grid gap-6 md:grid-cols-3">
            <Step
              n={1}
              title="Maak een account"
              text="Gratis voor trainers. Je club koppel je later met een uitnodiging van je clubbeheerder."
            />
            <Step
              n={2}
              title="Schrijf of vind oefeningen"
              text="Alles wat je maakt is eerst privé. Deel een oefening met je club of met iedereen; een beheerder kijkt mee."
            />
            <Step
              n={3}
              title="Neem het mee de zaal in"
              text="Grote knoppen en goed leesbaar op je telefoon, ook in fel tl-licht en op armlengte."
            />
          </ol>
        </section>
      </div>

      <section className="mx-4 mb-4 grid justify-items-start gap-5 rounded-3xl bg-foreground bg-[image:radial-gradient(70%_120%_at_100%_100%,color-mix(in_oklab,var(--primary)_55%,transparent),transparent_65%)] px-6 py-12 text-background md:mx-8 md:mb-6 md:p-16">
        <h2 className="max-w-[20ch] text-h1 font-bold lg:text-h1-lg">
          Klaar voor je volgende training?
        </h2>
        <p className="max-w-[48ch] text-lg opacity-75">
          Maak een account en zet je eerste oefening erin. Je oefeningen blijven van jou, ook als je
          van club wisselt.
        </p>
        <Button asChild size="lg" className="bg-background text-foreground hover:bg-background/90">
          <Link to="/registreren">
            Account aanmaken
            <ArrowRight aria-hidden="true" />
          </Link>
        </Button>
      </section>

      <footer className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-4 pt-6 pb-10 text-sm text-muted-foreground md:px-8">
        <span>© {new Date().getFullYear()} Train2Volley</span>
        <nav aria-label="Voettekst" className="flex gap-4">
          <Link to="/inloggen" className="underline-offset-4 hover:underline">
            Inloggen
          </Link>
          <Link to="/registreren" className="underline-offset-4 hover:underline">
            Account aanmaken
          </Link>
        </nav>
      </footer>
    </div>
  );
}

/** Zachte gloed achter de hero; staat stil bij prefers-reduced-motion (index.css). */
function Blobs() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 -z-10 overflow-hidden [mask-image:linear-gradient(180deg,black_70%,transparent)]"
    >
      <span className="animate-float absolute -top-56 -right-36 size-[620px] rounded-full bg-primary opacity-30 blur-[90px] dark:opacity-55" />
      <span className="animate-float absolute top-[38%] -left-30 size-[420px] rounded-full bg-[oklch(0.828_0.189_84.429)] opacity-15 blur-[90px] [animation-delay:-6s]" />
      <span className="animate-float absolute right-[22%] -bottom-16 size-[380px] rounded-full bg-[oklch(0.645_0.246_16.439)] opacity-15 blur-[90px] [animation-delay:-12s]" />
    </div>
  );
}

/** Productvoorbeeld opgebouwd uit dezelfde stijl als de app; voorbeeldinhoud, geen echte data. */
function Preview() {
  return (
    <figure
      className="relative grid gap-3 rounded-3xl border bg-card/70 p-5 shadow-xl backdrop-blur-md"
      aria-label="Voorbeeld van een oefening in Train2Volley"
    >
      <figcaption className="absolute -top-3 left-5 rounded-full border bg-background px-2.5 py-0.5 text-caption font-medium text-muted-foreground">
        Voorbeeld
      </figcaption>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary">Pass/receptie</Badge>
        <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
          <Users className="size-3.5" aria-hidden="true" />
          Club
        </span>
      </div>
      <p className="text-h3 font-semibold">Butterfly met drie lijnen</p>
      <p className="text-sm text-muted-foreground">
        Pass, set-up en aanval in een doorlopende rotatie.
      </p>
      <div className="flex gap-4 text-sm text-muted-foreground tabular-nums">
        <span className="flex items-center gap-1.5">
          <Clock className="size-3.5" aria-hidden="true" />
          15 min
        </span>
        <span className="flex items-center gap-1.5">
          <Users className="size-3.5" aria-hidden="true" />
          6–12
        </span>
      </div>
      <ol className="mt-1 grid gap-2">
        {[
          'Zet drie rijen op: positie 5, 2/3 en 4.',
          'Passer speelt naar de set-upper en sluit aan.',
          'Aanvaller slaat aan en haalt de bal op.',
        ].map((step, i) => (
          <li
            key={step}
            className="flex items-start gap-3 rounded-lg border bg-background/80 px-3 py-2.5 text-sm"
          >
            <span className="font-semibold text-muted-foreground tabular-nums">{i + 1}</span>
            {step}
          </li>
        ))}
      </ol>
    </figure>
  );
}

function Benefit(props: { icon: LucideIcon; title: string; text: string; soon?: boolean }) {
  return (
    <div className="grid content-start gap-3 rounded-xl border bg-card/80 p-6 transition-[transform,border-color] duration-200 hover:-translate-y-0.5 hover:border-primary/45">
      <span className="grid size-11 place-items-center rounded-md bg-linear-135 from-primary to-[oklch(0.645_0.246_16.439)] text-white">
        <props.icon className="size-5" aria-hidden="true" />
      </span>
      <h3 className="flex flex-wrap items-center gap-2 text-h3 font-semibold">
        {props.title}
        {props.soon && <Badge variant="outline">Binnenkort</Badge>}
      </h3>
      <p className="text-muted-foreground">{props.text}</p>
    </div>
  );
}

function Step(props: { n: number; title: string; text: string }) {
  return (
    <li className="grid content-start gap-2.5 border-t-2 border-foreground pt-5">
      <span className="text-sm font-bold text-primary tabular-nums">0{props.n}</span>
      <h3 className="text-h3 font-semibold">{props.title}</h3>
      <p className="text-muted-foreground">{props.text}</p>
    </li>
  );
}
